import { isInChottoAppSync } from '@/lib/browser';

declare const __CAPACITOR_SERVER_URL__: string;
const CAPACITOR_SERVER_URL = __CAPACITOR_SERVER_URL__;

/**
 * Builds the full API URL, using absolute URL when running inside the native
 * Chotto app on local files (capacitor:// or file://), relative URL otherwise.
 * If the URL already includes a protocol, it is returned as-is.
 */
export function buildApiUrl(apiUrl: string): string {
  // Don't modify URLs that already have a protocol
  if (apiUrl.includes('://')) {
    return apiUrl;
  }
  const isLocalFiles = !window.location.protocol.startsWith('http');
  if (isInChottoAppSync() && isLocalFiles) {
    return `${CAPACITOR_SERVER_URL}${apiUrl}`;
  }
  return apiUrl;
}

/**
 * Fetch wrapper that handles URL transformation for native app compatibility.
 * Returns the raw Response for flexibility (caller handles JSON parsing, blobs, etc.)
 */
export async function apiFetch(
  apiUrl: string,
  options?: RequestInit
): Promise<Response> {
  const url = buildApiUrl(apiUrl);
  return fetch(url, options);
}

/**
 * Returns the WebSocket host to use.
 * When running in the native app on local files, returns the host from capacitor config.
 * Otherwise returns window.location.host.
 */
export function getWebSocketHost(): string {
  const isLocalFiles = !window.location.protocol.startsWith('http');
  if (isInChottoAppSync() && isLocalFiles) {
    // Extract host from CAPACITOR_SERVER_URL (e.g., "https://chotto.chat" -> "chotto.chat")
    return CAPACITOR_SERVER_URL.replace(/^https?:\/\//, '');
  }
  return window.location.host;
}

/**
 * Returns the WebSocket protocol to use (ws:// or wss://).
 * When running in the native app on local files, returns wss:// if capacitor URL is https.
 * Otherwise uses the current page protocol.
 */
export function getWebSocketProtocol(): string {
  const isLocalFiles = !window.location.protocol.startsWith('http');
  if (isInChottoAppSync() && isLocalFiles) {
    return CAPACITOR_SERVER_URL.startsWith('https') ? 'wss://' : 'ws://';
  }
  return window.location.protocol === 'https:' ? 'wss://' : 'ws://';
}
